import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { haatSignLogoIc } from "../../assets";
import { getResourcePath, ROUTES } from "../../shared/constants/routes";
import {
  getLocalizedPageDescription,
  getLocalizedPageKeywords,
  getLocalizedPageTitle,
  getLocalizedSectionTitle,
  groupResourcePagesBySection,
  resourceNavigationPages,
} from "../resources/resourceRegistry";
import "../styles/resourcePages.css";

const iconKindsBySection = {
  "getting-started": "compass",
  "account-access": "shield",
  "sharing-signing": "share",
  "sharing-signing-experience": "share",
  "signing-experience": "pen",
  "templates-fields": "layers",
  "field-tools": "sliders",
  submissions: "inbox",
  "administration-integrations": "settings",
  "reports-validation": "chart",
  "reports-audit-validation": "chart",
  "audit-certificates": "badge",
  "pdf-documents": "document",
  "document-management": "document",
  cleanup: "archive",
  other: "spark",
};

const highlightSectionKeys = [
  "getting-started",
  "templates-fields",
  "submissions",
];

const highlightDescriptionKeys = {
  "getting-started": "resourcesHome.highlightDescriptions.gettingStarted",
  "templates-fields": "resourcesHome.highlightDescriptions.templatesFields",
  submissions: "resourcesHome.highlightDescriptions.submissions",
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </svg>
);

const filterResourcePages = (pages, query, t) => {
  const normalized = query.trim().toLowerCase();

  if (!normalized) {
    return pages;
  }

  return pages.filter((page) => {
    const title = getLocalizedPageTitle(page, t);
    const description = getLocalizedPageDescription(page, t);
    const translatedKeywords = getLocalizedPageKeywords(page, t);
    const keywords = [
      ...(page.keywords || []),
      ...(Array.isArray(translatedKeywords) ? translatedKeywords : []),
    ];

    return [title, description, ...keywords]
      .join(" ")
      .toLowerCase()
      .includes(normalized);
  });
};

const SectionIcon = ({ kind }) => {
  const sharedProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  switch (kind) {
    case "compass":
      return (
        <svg {...sharedProps}>
          <circle cx="12" cy="12" r="8" />
          <path d="m14.8 9.2-2.8 5.6-2.8 1.2 1.2-2.8 5.6-2.8Z" />
        </svg>
      );
    case "shield":
      return (
        <svg {...sharedProps}>
          <path d="m12 3 7 3v5c0 4.6-2.8 8.4-7 10-4.2-1.6-7-5.4-7-10V6l7-3Z" />
          <path d="m9.5 12 1.7 1.7 3.3-3.7" />
        </svg>
      );
    case "share":
      return (
        <svg {...sharedProps}>
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="18" cy="6" r="2.5" />
          <circle cx="18" cy="18" r="2.5" />
          <path d="m8.2 10.8 7.6-3.6" />
          <path d="m8.2 13.2 7.6 3.6" />
        </svg>
      );
    case "pen":
      return (
        <svg {...sharedProps}>
          <path d="M12 19H5l1.2-4.2L14.7 6.3a2.1 2.1 0 0 1 3 3L9.3 17.7Z" />
          <path d="m13.8 7.2 3 3" />
        </svg>
      );
    case "layers":
      return (
        <svg {...sharedProps}>
          <path d="m12 4 8 4-8 4-8-4 8-4Z" />
          <path d="m4 12 8 4 8-4" />
          <path d="m4 16 8 4 8-4" />
        </svg>
      );
    case "sliders":
      return (
        <svg {...sharedProps}>
          <path d="M4 6h16" />
          <path d="M4 12h16" />
          <path d="M4 18h16" />
          <circle cx="9" cy="6" r="2" />
          <circle cx="15" cy="12" r="2" />
          <circle cx="11" cy="18" r="2" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...sharedProps}>
          <path d="M5 5h14l1 8v4H4v-4l1-8Z" />
          <path d="M4 13h4l2 3h4l2-3h4" />
        </svg>
      );
    case "settings":
      return (
        <svg {...sharedProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3" />
          <path d="M12 19v3" />
          <path d="m4.9 4.9 2.1 2.1" />
          <path d="m17 17 2.1 2.1" />
          <path d="M2 12h3" />
          <path d="M19 12h3" />
          <path d="m4.9 19.1 2.1-2.1" />
          <path d="M17 7 19.1 4.9" />
        </svg>
      );
    case "code":
      return (
        <svg {...sharedProps}>
          <path d="m9 18-6-6 6-6" />
          <path d="m15 6 6 6-6 6" />
          <path d="m13 4-2 16" />
        </svg>
      );
    case "chart":
      return (
        <svg {...sharedProps}>
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20V8" />
        </svg>
      );
    case "badge":
      return (
        <svg {...sharedProps}>
          <path d="m12 3 2.5 5 5.5.8-4 3.9.9 5.5-4.9-2.6-4.9 2.6.9-5.5-4-3.9 5.5-.8Z" />
          <path d="m9 17-1 4 4-2 4 2-1-4" />
        </svg>
      );
    case "lock":
      return (
        <svg {...sharedProps}>
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
      );
    case "document":
      return (
        <svg {...sharedProps}>
          <path d="M7 3h7l5 5v13H7Z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6" />
          <path d="M9 17h6" />
        </svg>
      );
    case "phone":
      return (
        <svg {...sharedProps}>
          <rect x="7" y="2" width="10" height="20" rx="2" />
          <path d="M11 18h2" />
        </svg>
      );
    case "workflow":
      return (
        <svg {...sharedProps}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="6" cy="18" r="2" />
          <circle cx="18" cy="18" r="2" />
          <path d="M8 6h8" />
          <path d="M6 8v8" />
          <path d="M18 8v8" />
          <path d="M8 18h8" />
        </svg>
      );
    case "archive":
      return (
        <svg {...sharedProps}>
          <path d="M4 7h16v4H4Z" />
          <path d="M6 11v8h12v-8" />
          <path d="M10 15h4" />
        </svg>
      );
    default:
      return (
        <svg {...sharedProps}>
          <path d="m12 3 1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7Z" />
          <path d="m19 15 .8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8Z" />
        </svg>
      );
  }
};

const ResourcesPage = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const searchWrapRef = useRef(null);
  const languageMenuRef = useRef(null);

  const allSections = useMemo(
    () => groupResourcePagesBySection(resourceNavigationPages),
    [],
  );

  const primaryPagesBySection = useMemo(
    () =>
      new Map(allSections.map((section) => [section.key, section.items[0]])),
    [allSections],
  );

  const filteredPages = useMemo(
    () => filterResourcePages(resourceNavigationPages, query, t),
    [query, t],
  );

  const groupedSections = allSections;

  const suggestedPages = useMemo(
    () => (query.trim() ? filteredPages.slice(0, 6) : []),
    [filteredPages, query],
  );

  const highlightSections = useMemo(
    () =>
      highlightSectionKeys
        .map((key) => allSections.find((section) => section.key === key))
        .filter(Boolean),
    [allSections],
  );

  const lastRowRemainder = groupedSections.length % 4;
  const currentLanguage =
    (i18n.resolvedLanguage || i18n.language) === "ar" ? "ar" : "en";
  const languageOptions = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
  ];
  const selectedLanguageOption =
    languageOptions.find((option) => option.code === currentLanguage) ||
    languageOptions[0];

  const showSuggestions = searchFocused && query.trim().length > 0;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(event.target)
      ) {
        setSearchFocused(false);
      }

      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSearchFocused(false);
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSuggestionSelect = (slug) => {
    setSearchFocused(false);
    setQuery("");
    navigate(getResourcePath(slug));
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && suggestedPages.length > 0) {
      event.preventDefault();
      handleSuggestionSelect(suggestedPages[0].slug);
    }
  };

  const getLanguageOptionLabel = (option) => option.label;

  const handleLanguageSelect = (langCode) => {
    setLanguageMenuOpen(false);
    if (langCode !== currentLanguage) {
      i18n.changeLanguage(langCode);
    }
  };

  return (
    <main className="resources-home-page" dir={i18n.dir()}>
      <div className="resources-home-shell">
        <header className="resources-home-topbar">
          <div className="resources-home-content resources-home-topbar-inner">
            <Link
              to={ROUTES.RESOURCES}
              className="resources-home-brand"
              aria-label={t("resourcesHome.brandLabel")}
            >
              <img
                src={haatSignLogoIc}
                alt="HaatSign"
                className="resources-home-brand-logo"
              />
            </Link>

            <div className="resources-home-topbar-actions">
              <a
                className="resources-home-login"
                href="https://app.haatsign.sa/login"
                target="_blank"
                rel="noreferrer"
              >
                {t("resourcesHome.login")}
              </a>
            </div>
          </div>
        </header>

        <section
          className="resources-home-hero"
          style={{
            "--resources-home-hero-image": `url(${process.env.PUBLIC_URL}/docs/assets/Screenshots/Banner.png)`,
          }}
        >
          <div className="resources-home-content resources-home-hero-inner">
            <p className="resources-home-eyebrow">
              {t("resourcesHome.eyebrow")}
            </p>
            <h1 className="resources-home-title">{t("resourcesHome.title")}</h1>
            <p className="resources-home-subtitle">
              {t("resourcesHome.subtitle")}
            </p>

            <div className="resources-home-actions">
              <Link
                className="resources-home-hero-btn primary"
                to={getResourcePath("quick-start")}
              >
                {t("resourcesHome.primaryAction")}
              </Link>

              <a
                className="resources-home-hero-btn secondary"
                href="#resources-home-categories"
              >
                {t("resourcesHome.secondaryAction")}
              </a>
            </div>

            <div className="resources-home-search-wrap" ref={searchWrapRef}>
              <label className="sr-only" htmlFor="resources-home-search">
                {t("resourcesHome.searchLabel")}
              </label>

              <div className="resources-home-search">
                <input
                  id="resources-home-search"
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSearchFocused(true);
                  }}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t("resourcesHome.searchPlaceholder")}
                  autoComplete="off"
                />

                <span className="resources-home-search-icon">
                  <SearchIcon />
                </span>
              </div>

              {showSuggestions && (
                <div
                  className="resources-home-suggestions"
                  role="listbox"
                  aria-label={t("resourcesHome.searchSuggestionsLabel")}
                >
                  {suggestedPages.length > 0 ? (
                    suggestedPages.map((page) => (
                      <button
                        key={page.slug}
                        type="button"
                        className="resources-home-suggestion"
                        onClick={() => handleSuggestionSelect(page.slug)}
                      >
                        <span className="resources-home-suggestion-title">
                          {getLocalizedPageTitle(page, t)}
                        </span>
                        <span className="resources-home-suggestion-description">
                          {getLocalizedPageDescription(page, t)}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="resources-home-suggestions-empty">
                      {t("resourcesHome.searchSuggestionsEmpty")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <section
          className="resources-home-highlights"
          id="resources-home-highlights"
        >
          <div className="resources-home-content">
            <div className="resources-home-section-heading">
              <h2 className="resources-home-section-title">
                {t("resourcesHome.highlightsTitle")}
              </h2>
              <p className="resources-home-section-text">
                {t("resourcesHome.highlightsSubtitle")}
              </p>
            </div>

            <div className="resources-home-feature-grid">
              {highlightSections.map((section) => (
                <article
                  key={section.key}
                  className="resources-home-feature-card"
                >
                  <span className="resources-home-feature-icon">
                    <SectionIcon kind={iconKindsBySection[section.key]} />
                  </span>
                  <h3 className="resources-home-feature-title">
                    {getLocalizedSectionTitle(section, t)}
                  </h3>
                  <p className="resources-home-feature-description">
                    {t(highlightDescriptionKeys[section.key])}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="resources-home-categories"
          id="resources-home-categories"
        >
          <div className="resources-home-content">
            <div className="resources-home-section-heading">
              <p className="resources-home-section-kicker">
                {t("resourcesHome.categoriesKicker")}
              </p>
              <h2 className="resources-home-section-title">
                {t("resourcesHome.browseTitle")}
              </h2>
              <p className="resources-home-section-text">
                {t("resourcesHome.browseSubtitle")}
              </p>
            </div>

            {groupedSections.length === 0 ? (
              <div className="resources-home-empty">
                <p>{t("resourcesHome.noSearchResults")}</p>
                <button
                  type="button"
                  className="resources-home-clear"
                  onClick={() => setQuery("")}
                >
                  {t("resourcesHome.clearSearch")}
                </button>
              </div>
            ) : (
              <div
                className={`resources-home-grid${
                  lastRowRemainder ? ` remainder-${lastRowRemainder}` : ""
                }`}
                aria-label={t("resourcesHome.categoriesAriaLabel")}
              >
                {groupedSections.map((section) => {
                  const featuredPages = section.items.slice(0, 4);
                  const primaryPage =
                    primaryPagesBySection.get(section.key) || section.items[0];
                  const totalCount =
                    allSections.find((entry) => entry.key === section.key)
                      ?.items.length || section.items.length;
                  const remainingCount = Math.max(
                    totalCount - featuredPages.length,
                    0,
                  );

                  return (
                    <article
                      key={section.key}
                      className="resource-category-card"
                    >
                      <div className="resource-category-icon-wrap">
                        <span className="resource-category-icon">
                          <SectionIcon kind={iconKindsBySection[section.key]} />
                        </span>
                      </div>

                      <div className="resource-category-card-body">
                        <p className="resource-category-count">
                          {t("resourcesHome.guidesCount", {
                            count: totalCount,
                          })}
                        </p>
                        <h3 className="resource-category-title">
                          {getLocalizedSectionTitle(section, t)}
                        </h3>
                        <p className="resource-category-summary">
                          {getLocalizedPageDescription(primaryPage, t)}
                        </p>

                        <ul className="resource-category-page-list">
                          {featuredPages.map((page) => (
                            <li key={page.slug}>
                              <Link
                                className="resource-category-page"
                                to={getResourcePath(page.slug)}
                              >
                                <span className="resource-category-page-title">
                                  {getLocalizedPageTitle(page, t)}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="resource-category-footer">
                        {remainingCount > 0 && (
                          <span className="resource-category-more">
                            {t("resourcesHome.moreArticles", {
                              count: remainingCount,
                            })}
                          </span>
                        )}

                        <Link
                          className="resource-category-read-more"
                          to={getResourcePath(primaryPage.slug)}
                        >
                          {t("resourcesHome.readMore")}
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <footer className="resources-home-footer">
          <div className="resources-home-content resources-home-footer-inner">
            <div className="resources-home-footer-language">
              <div className="lang-dropdown" ref={languageMenuRef}>
                <button
                  type="button"
                  className={`lang-dropdown-toggle${languageMenuOpen ? " open" : ""}`}
                  onClick={() => setLanguageMenuOpen((prev) => !prev)}
                  aria-label={t("language.toggleLabel")}
                  aria-haspopup="menu"
                  aria-expanded={languageMenuOpen}
                >
                  <span className="lang-toggle-main">
                    <span className="lang-toggle-globe" aria-hidden="true">
                      <svg viewBox="0 0 24 24" focusable="false">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20" />
                        <path d="M12 2c3.2 3 3.2 17 0 20" />
                        <path d="M12 2c-3.2 3-3.2 17 0 20" />
                        <path d="M5 7.5h14" />
                        <path d="M5 16.5h14" />
                      </svg>
                    </span>
                    <span className="lang-current-label">
                      {getLanguageOptionLabel(selectedLanguageOption)}
                    </span>
                  </span>

                  <span className="lang-dropdown-caret" aria-hidden="true" />
                </button>

                {languageMenuOpen && (
                  <div className="lang-dropdown-menu" role="menu">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        className={`lang-menu-item${
                          option.code === currentLanguage ? " selected" : ""
                        }`}
                        role="menuitemradio"
                        aria-checked={option.code === currentLanguage}
                        onClick={() => handleLanguageSelect(option.code)}
                        title={option.label}
                        aria-label={option.label}
                      >
                        <span className="lang-item-label">
                          {getLanguageOptionLabel(option)}
                        </span>
                        {option.code === currentLanguage && (
                          <span className="lang-item-check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="resources-home-footer-powered">
              {t("footer.poweredBy")}{" "}
              <a
                href="https://app.haatsign.sa"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("footer.haatSign")}
              </a>
            </div>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default ResourcesPage;
